import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Newspaper,
  Calendar,
  Users,
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Globe,
  RefreshCw,
  X,
  Bell,
  ChevronUp,
  ChevronDown,
  Award,
  Sparkles,
} from "lucide-react";
import LoadingSpinner from "../components/common/LoadingSpinner";
import NewsFormModal from "../components/modals/NewsFormModal";
import EventFormModal from "../components/modals/EventFormModal";
import StaffFormModal from "../components/modals/StaffFormModal";
import CourseFormModal from "../components/modals/CourseFormModal";
import PopupFormModal from "../components/modals/PopupFormModal";
import PartnerFormModal from "../components/modals/PartnerFormModal";
import FloatingBadgeFormModal from "../components/modals/FloatingBadgeFormModal";
import { newsService } from "../services/newsService";
import { eventService } from "../services/eventService";
import { staffService } from "../services/staffService";
import { publicCourseService } from "../services/publicCourseService";
import { popupService } from "../services/popupService";
import { partnerService } from "../services/partnerService";
import { floatingBadgeService } from "../services/floatingBadgeService";
import { uploadService } from "../services/uploadService";
import {
  News,
  Event,
  Staff,
  PublicCourse,
  Popup,
  Partner,
  FloatingBadge,
} from "../types/landing";

const LandingPageManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    | "news"
    | "events"
    | "staff"
    | "courses"
    | "popups"
    | "partners"
    | "badges"
  >("news");
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<PublicCourse | null>(
    null
  );
  const [showPopupModal, setShowPopupModal] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState<Popup | null>(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<FloatingBadge | null>(
    null
  );

  // Fetch data for each section
  const {
    data: newsData,
    isLoading: newsLoading,
    refetch: refetchNews,
  } = useQuery({
    queryKey: ["landing-news"],
    queryFn: async () => {
      const result = await newsService.getAllNews({ page: 1, limit: 50 });
      return result;
    },
  });

  const {
    data: eventsData,
    isLoading: eventsLoading,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ["landing-events"],
    queryFn: async () => {
      const result = await eventService.getAllEvents({ page: 1, limit: 50 });
      return result;
    },
  });

  const {
    data: staffData,
    isLoading: staffLoading,
    refetch: refetchStaff,
  } = useQuery({
    queryKey: ["landing-staff"],
    queryFn: async () => {
      const result = await staffService.getAllStaff({ page: 1, limit: 50 });
      return result;
    },
  });

  const {
    data: coursesData,
    isLoading: coursesLoading,
    refetch: refetchCourses,
  } = useQuery({
    queryKey: ["landing-courses"],
    queryFn: async () => {
      const result = await publicCourseService.getAllCourses({
        page: 1,
        limit: 50,
      });
      // Sort courses by displayOrder
      if (result.data) {
        result.data.sort((a: any, b: any) => {
          const orderA = a.displayOrder ?? 0;
          const orderB = b.displayOrder ?? 0;
          return orderA - orderB;
        });
      }
      return result;
    },
  });

  const {
    data: popupsData,
    isLoading: popupsLoading,
    refetch: refetchPopups,
  } = useQuery({
    queryKey: ["landing-popups"],
    queryFn: async () => {
      const result = await popupService.getAllPopups({ page: 1, limit: 50 });
      return result;
    },
  });

  const {
    data: partnersData,
    isLoading: partnersLoading,
    refetch: refetchPartners,
  } = useQuery({
    queryKey: ["landing-partners"],
    queryFn: async () => {
      const result = await partnerService.getAllPartners({
        page: 1,
        limit: 50,
      });
      // Sort partners by displayOrder
      if (result.data) {
        result.data.sort((a: any, b: any) => {
          const orderA = a.displayOrder ?? 0;
          const orderB = b.displayOrder ?? 0;
          return orderA - orderB;
        });
      }
      return result;
    },
  });

  const {
    data: badgesData,
    isLoading: badgesLoading,
    refetch: refetchBadges,
  } = useQuery({
    queryKey: ["landing-floating-badges"],
    queryFn: async () => {
      const result = await floatingBadgeService.getAllFloatingBadges({
        page: 1,
        limit: 50,
      });
      // Sort badges by displayOrder
      if (result.data) {
        result.data.sort((a: any, b: any) => {
          const orderA = a.displayOrder ?? 0;
          const orderB = b.displayOrder ?? 0;
          return orderA - orderB;
        });
      }
      return result;
    },
  });

  const tabs = [
    {
      id: "news",
      label: "News",
      icon: Newspaper,
      count: newsData?.pagination?.totalItems || 0,
    },
    {
      id: "events",
      label: "Events",
      icon: Calendar,
      count: eventsData?.pagination?.totalItems || 0,
    },
    {
      id: "staff",
      label: "Staff",
      icon: Users,
      count: staffData?.pagination?.totalItems || 0,
    },
    {
      id: "courses",
      label: "Courses",
      icon: BookOpen,
      count: coursesData?.pagination?.totalItems || 0,
    },
    {
      id: "popups",
      label: "Popups",
      icon: Bell,
      count: popupsData?.pagination?.totalItems || 0,
    },
    {
      id: "partners",
      label: "Partners",
      icon: Award,
      count: partnersData?.pagination?.totalItems || 0,
    },
    {
      id: "badges",
      label: "Floating Badges",
      icon: Sparkles,
      count: badgesData?.pagination?.totalItems || 0,
    },
  ];

  const handleRefresh = () => {
    switch (activeTab) {
      case "news":
        refetchNews();
        break;
      case "events":
        refetchEvents();
        break;
      case "staff":
        refetchStaff();
        break;
      case "courses":
        refetchCourses();
        break;
      case "popups":
        refetchPopups();
        break;
      case "partners":
        refetchPartners();
        break;
      case "badges":
        refetchBadges();
        break;
    }
  };

  const handleCreateNews = () => {
    setSelectedNews(null);
    setShowNewsModal(true);
  };

  const handleEditNews = (news: News) => {
    setSelectedNews(news);
    setShowNewsModal(true);
  };

  const handleNewsModalClose = () => {
    setShowNewsModal(false);
    setSelectedNews(null);
  };

  const handleNewsSuccess = () => {
    refetchNews();
  };

  const handlePublishNews = async (newsId: string) => {
    try {
      await newsService.publishNews(newsId);
      toast.success("News published successfully!");
      refetchNews();
    } catch (error: any) {
      toast.error("Failed to publish news");
    }
  };

  const handleUnpublishNews = async (newsId: string) => {
    try {
      await newsService.unpublishNews(newsId);
      toast.success("News unpublished successfully!");
      refetchNews();
    } catch (error: any) {
      toast.error("Failed to unpublish news");
    }
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleEventModalClose = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleEventSuccess = () => {
    refetchEvents();
  };

  const handlePublishEvent = async (eventId: string) => {
    try {
      await eventService.publishEvent(eventId);
      toast.success("Event published successfully!");
      refetchEvents();
    } catch (error: any) {
      toast.error("Failed to publish event");
    }
  };

  const handleUnpublishEvent = async (eventId: string) => {
    try {
      await eventService.unpublishEvent(eventId);
      toast.success("Event unpublished successfully!");
      refetchEvents();
    } catch (error: any) {
      toast.error("Failed to unpublish event");
    }
  };

  const handleCancelEvent = async (eventId: string) => {
    try {
      await eventService.cancelEvent(eventId);
      toast.success("Event cancelled successfully!");
      refetchEvents();
    } catch (error: any) {
      toast.error("Failed to cancel event");
    }
  };

  const handleCreateStaff = () => {
    setSelectedStaff(null);
    setShowStaffModal(true);
  };

  const handleEditStaff = (staff: Staff) => {
    setSelectedStaff(staff);
    setShowStaffModal(true);
  };

  const handleStaffModalClose = () => {
    setShowStaffModal(false);
    setSelectedStaff(null);
  };

  const handleStaffSuccess = () => {
    refetchStaff();
  };

  const handleActivateStaff = async (staffId: string) => {
    try {
      await staffService.activateStaff(staffId);
      toast.success("Staff activated successfully!");
      refetchStaff();
    } catch (error: any) {
      toast.error("Failed to activate staff");
    }
  };

  const handleDeactivateStaff = async (staffId: string) => {
    try {
      await staffService.deactivateStaff(staffId);
      toast.success("Staff deactivated successfully!");
      refetchStaff();
    } catch (error: any) {
      toast.error("Failed to deactivate staff");
    }
  };

  // Renumbers the whole list sequentially so reordering never depends on
  // (possibly duplicate/conflicting) displayOrder values set on the form.
  const moveStaff = async (staff: Staff, direction: "up" | "down") => {
    const allStaff = staffData?.data || [];
    const currentIndex = allStaff.findIndex((s: Staff) => s._id === staff._id);
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= allStaff.length) {
      return;
    }

    const reordered = [...allStaff];
    [reordered[currentIndex], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[currentIndex],
    ];

    try {
      await staffService.reorderStaff(
        reordered.map((s: Staff, index: number) => ({
          id: s._id,
          displayOrder: index,
        }))
      );
      toast.success("Staff order updated!");
      refetchStaff();
    } catch (error: any) {
      toast.error("Failed to update staff order");
    }
  };

  const handleMoveStaffUp = (staff: Staff) => moveStaff(staff, "up");
  const handleMoveStaffDown = (staff: Staff) => moveStaff(staff, "down");

  const handleCreateCourse = () => {
    setSelectedCourse(null);
    setShowCourseModal(true);
  };

  const handleEditCourse = (course: PublicCourse) => {
    setSelectedCourse(course);
    setShowCourseModal(true);
  };

  const handleCourseModalClose = () => {
    setShowCourseModal(false);
    setSelectedCourse(null);
  };

  const handleCourseSuccess = () => {
    refetchCourses();
  };

  const handleActivateCourse = async (courseId: string) => {
    try {
      await publicCourseService.activateCourse(courseId);
      toast.success("Course activated successfully!");
      refetchCourses();
    } catch (error: any) {
      toast.error("Failed to activate course");
    }
  };

  const handleDeactivateCourse = async (courseId: string) => {
    try {
      await publicCourseService.deactivateCourse(courseId);
      toast.success("Course deactivated successfully!");
      refetchCourses();
    } catch (error: any) {
      toast.error("Failed to deactivate course");
    }
  };

  const handleDeleteNews = async (news: News) => {
    if (
      !confirm(
        "Are you sure you want to delete this news item? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete image from R2 if it exists
      if (news.imageUrl) {
        // Extract the key from the URL (assuming URL format includes the key)
        const urlParts = news.imageUrl.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(`landingpage/news/${filename}`);
      }

      // Delete the news item
      await newsService.deleteNews(news._id);
      toast.success("News deleted successfully!");
      refetchNews();
    } catch (error: any) {
      toast.error("Failed to delete news");
    }
  };

  const handleDeleteEvent = async (event: Event) => {
    if (
      !confirm(
        "Are you sure you want to delete this event? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete image from R2 if it exists
      if (event.imageUrl) {
        const urlParts = event.imageUrl.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(`landingpage/events/${filename}`);
      }

      // Delete the event
      await eventService.deleteEvent(event._id);
      toast.success("Event deleted successfully!");
      refetchEvents();
    } catch (error: any) {
      toast.error("Failed to delete event");
    }
  };

  const handleDeleteStaff = async (staff: Staff) => {
    if (
      !confirm(
        "Are you sure you want to delete this staff member? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete image from R2 if it exists
      if (staff.imageUrl) {
        const urlParts = staff.imageUrl.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(`landingpage/staff/${filename}`);
      }

      // Delete the staff member
      await staffService.deleteStaff(staff._id);
      toast.success("Staff deleted successfully!");
      refetchStaff();
    } catch (error: any) {
      toast.error("Failed to delete staff");
    }
  };

  const handleDeleteCourse = async (course: PublicCourse) => {
    if (
      !confirm(
        "Are you sure you want to delete this course? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete image from R2 if it exists
      if (course.imageUrl) {
        const urlParts = course.imageUrl.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(`landingpage/courses/${filename}`);
      }

      // Delete the course
      await publicCourseService.deleteCourse(course._id);
      toast.success("Course deleted successfully!");
      refetchCourses();
    } catch (error: any) {
      toast.error("Failed to delete course");
    }
  };

  const handleMoveCourseUp = async (course: PublicCourse, index: number) => {
    if (index === 0) return; // Already at top

    try {
      const courses = coursesData?.data || [];
      const prevCourse = courses[index - 1];

      // Swap displayOrder values
      const currentOrder = course.displayOrder ?? index;
      const prevOrder = prevCourse.displayOrder ?? index - 1;

      await publicCourseService.updateCourseOrder(course._id, prevOrder);
      await publicCourseService.updateCourseOrder(prevCourse._id, currentOrder);

      toast.success("Course moved up!");
      refetchCourses();
    } catch (error: any) {
      toast.error("Failed to reorder course");
    }
  };

  const handleMoveCourseDown = async (course: PublicCourse, index: number) => {
    const courses = coursesData?.data || [];
    if (index === courses.length - 1) return; // Already at bottom

    try {
      const nextCourse = courses[index + 1];

      // Swap displayOrder values
      const currentOrder = course.displayOrder ?? index;
      const nextOrder = nextCourse.displayOrder ?? index + 1;

      await publicCourseService.updateCourseOrder(course._id, nextOrder);
      await publicCourseService.updateCourseOrder(nextCourse._id, currentOrder);

      toast.success("Course moved down!");
      refetchCourses();
    } catch (error: any) {
      toast.error("Failed to reorder course");
    }
  };

  // Popup handlers
  const handleCreatePopup = () => {
    setSelectedPopup(null);
    setShowPopupModal(true);
  };

  const handleEditPopup = (popup: Popup) => {
    setSelectedPopup(popup);
    setShowPopupModal(true);
  };

  const handlePopupModalClose = () => {
    setShowPopupModal(false);
    setSelectedPopup(null);
  };

  const handlePopupSubmit = async (data: any) => {
    try {
      if (selectedPopup) {
        await popupService.updatePopup(selectedPopup._id, data);
        toast.success("Popup updated successfully!");
      } else {
        await popupService.createPopup(data);
        toast.success("Popup created successfully!");
      }
      refetchPopups();
      handlePopupModalClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to save popup");
    }
  };

  const handlePublishPopup = async (popupId: string) => {
    try {
      await popupService.publishPopup(popupId);
      toast.success("Popup published successfully!");
      refetchPopups();
    } catch (error: any) {
      toast.error("Failed to publish popup");
    }
  };

  const handleUnpublishPopup = async (popupId: string) => {
    try {
      await popupService.unpublishPopup(popupId);
      toast.success("Popup unpublished successfully!");
      refetchPopups();
    } catch (error: any) {
      toast.error("Failed to unpublish popup");
    }
  };

  const handleDeletePopup = async (popup: Popup) => {
    if (
      !confirm(
        "Are you sure you want to delete this popup? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete image from R2 if it exists
      if (popup.image?.url) {
        const urlParts = popup.image.url.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(`popups/${filename}`);
      }

      // Delete the popup
      await popupService.deletePopup(popup._id);
      toast.success("Popup deleted successfully!");
      refetchPopups();
    } catch (error: any) {
      toast.error("Failed to delete popup");
    }
  };

  // Partner handlers
  const handleCreatePartner = () => {
    setSelectedPartner(null);
    setShowPartnerModal(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setShowPartnerModal(true);
  };

  const handlePartnerModalClose = () => {
    setShowPartnerModal(false);
    setSelectedPartner(null);
  };

  const handlePartnerSubmit = async (data: any) => {
    try {
      if (selectedPartner) {
        await partnerService.updatePartner(selectedPartner._id, data);
        toast.success("Partner updated successfully!");
      } else {
        await partnerService.createPartner(data);
        toast.success("Partner created successfully!");
      }
      refetchPartners();
      handlePartnerModalClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to save partner");
    }
  };

  const handleActivatePartner = async (partnerId: string) => {
    try {
      await partnerService.activatePartner(partnerId);
      toast.success("Partner activated successfully!");
      refetchPartners();
    } catch (error: any) {
      toast.error("Failed to activate partner");
    }
  };

  const handleDeactivatePartner = async (partnerId: string) => {
    try {
      await partnerService.deactivatePartner(partnerId);
      toast.success("Partner deactivated successfully!");
      refetchPartners();
    } catch (error: any) {
      toast.error("Failed to deactivate partner");
    }
  };

  const handleDeletePartner = async (partner: Partner) => {
    if (
      !confirm(
        "Are you sure you want to delete this partner? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete logo from R2 if it exists
      if (partner.logo?.url) {
        const urlParts = partner.logo.url.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(`landingpage/partners/${filename}`);
      }

      // Delete the partner
      await partnerService.deletePartner(partner._id);
      toast.success("Partner deleted successfully!");
      refetchPartners();
    } catch (error: any) {
      toast.error("Failed to delete partner");
    }
  };

  const handleMovePartnerUp = async (partner: Partner, index: number) => {
    if (index === 0) return; // Already at top

    try {
      const partners = partnersData?.data || [];
      const prevPartner = partners[index - 1];

      // Swap displayOrder values
      const currentOrder = partner.displayOrder ?? index;
      const prevOrder = prevPartner.displayOrder ?? index - 1;

      await partnerService.updatePartnerOrder(partner._id, prevOrder);
      await partnerService.updatePartnerOrder(prevPartner._id, currentOrder);

      toast.success("Partner moved up!");
      refetchPartners();
    } catch (error: any) {
      toast.error("Failed to reorder partner");
    }
  };

  const handleMovePartnerDown = async (partner: Partner, index: number) => {
    const partners = partnersData?.data || [];
    if (index === partners.length - 1) return; // Already at bottom

    try {
      const nextPartner = partners[index + 1];

      // Swap displayOrder values
      const currentOrder = partner.displayOrder ?? index;
      const nextOrder = nextPartner.displayOrder ?? index + 1;

      await partnerService.updatePartnerOrder(partner._id, nextOrder);
      await partnerService.updatePartnerOrder(nextPartner._id, currentOrder);

      toast.success("Partner moved down!");
      refetchPartners();
    } catch (error: any) {
      toast.error("Failed to reorder partner");
    }
  };

  // Floating Badge handlers
  const handleCreateBadge = () => {
    setSelectedBadge(null);
    setShowBadgeModal(true);
  };

  const handleEditBadge = (badge: FloatingBadge) => {
    setSelectedBadge(badge);
    setShowBadgeModal(true);
  };

  const handleBadgeModalClose = () => {
    setShowBadgeModal(false);
    setSelectedBadge(null);
  };

  const handleBadgeSubmit = async (data: any) => {
    try {
      if (selectedBadge) {
        await floatingBadgeService.updateFloatingBadge(
          selectedBadge._id,
          data
        );
        toast.success("Floating badge updated successfully!");
      } else {
        await floatingBadgeService.createFloatingBadge(data);
        toast.success("Floating badge created successfully!");
      }
      refetchBadges();
      handleBadgeModalClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to save floating badge");
    }
  };

  const handleActivateBadge = async (badgeId: string) => {
    try {
      await floatingBadgeService.activateFloatingBadge(badgeId);
      toast.success("Floating badge activated successfully!");
      refetchBadges();
    } catch (error: any) {
      toast.error("Failed to activate floating badge");
    }
  };

  const handleDeactivateBadge = async (badgeId: string) => {
    try {
      await floatingBadgeService.deactivateFloatingBadge(badgeId);
      toast.success("Floating badge deactivated successfully!");
      refetchBadges();
    } catch (error: any) {
      toast.error("Failed to deactivate floating badge");
    }
  };

  const handleDeleteBadge = async (badge: FloatingBadge) => {
    if (
      !confirm(
        "Are you sure you want to delete this floating badge? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete image from R2 if it exists
      if (badge.image?.url) {
        const urlParts = badge.image.url.split("/");
        const filename = urlParts[urlParts.length - 1];
        await uploadService.deleteFile(
          `landingpage/floating-badges/${filename}`
        );
      }

      // Delete the badge
      await floatingBadgeService.deleteFloatingBadge(badge._id);
      toast.success("Floating badge deleted successfully!");
      refetchBadges();
    } catch (error: any) {
      toast.error("Failed to delete floating badge");
    }
  };

  const handleMoveBadgeUp = async (badge: FloatingBadge, index: number) => {
    if (index === 0) return; // Already at top

    try {
      const badges = badgesData?.data || [];
      const prevBadge = badges[index - 1];

      // Swap displayOrder values
      const currentOrder = badge.displayOrder ?? index;
      const prevOrder = prevBadge.displayOrder ?? index - 1;

      await floatingBadgeService.updateFloatingBadgeOrder(
        badge._id,
        prevOrder
      );
      await floatingBadgeService.updateFloatingBadgeOrder(
        prevBadge._id,
        currentOrder
      );

      toast.success("Floating badge moved up!");
      refetchBadges();
    } catch (error: any) {
      toast.error("Failed to reorder floating badge");
    }
  };

  const handleMoveBadgeDown = async (badge: FloatingBadge, index: number) => {
    const badges = badgesData?.data || [];
    if (index === badges.length - 1) return; // Already at bottom

    try {
      const nextBadge = badges[index + 1];

      // Swap displayOrder values
      const currentOrder = badge.displayOrder ?? index;
      const nextOrder = nextBadge.displayOrder ?? index + 1;

      await floatingBadgeService.updateFloatingBadgeOrder(
        badge._id,
        nextOrder
      );
      await floatingBadgeService.updateFloatingBadgeOrder(
        nextBadge._id,
        currentOrder
      );

      toast.success("Floating badge moved down!");
      refetchBadges();
    } catch (error: any) {
      toast.error("Failed to reorder floating badge");
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "news":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  News Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage news articles displayed on the landing page
                </p>
              </div>
              <button
                onClick={handleCreateNews}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add News
              </button>
            </div>
            {newsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {newsData?.data?.map((item: any) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                            {item.title}
                          </h4>
                          <p className="text-sm text-slate-600 font-medium">
                            {new Date(item.publishDate).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                            item.status === "published"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>

                      {/* Image Preview */}
                      {item.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-40 object-cover rounded-lg shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        {item.status === "draft" ? (
                          <button
                            onClick={() => handlePublishNews(item._id)}
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                            title="Publish"
                          >
                            <Eye className="h-4 w-4" />
                            Publish
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnpublishNews(item._id)}
                            className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                            title="Unpublish"
                          >
                            <EyeOff className="h-4 w-4" />
                            Unpublish
                          </button>
                        )}
                        <button
                          onClick={() => handleEditNews(item)}
                          className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteNews(item)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "events":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Events Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage events displayed on the landing page
                </p>
              </div>
              <button
                onClick={handleCreateEvent}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Event
              </button>
            </div>
            {eventsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {eventsData?.data?.map((item: any) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                            {item.title}
                          </h4>
                          <p className="text-sm text-slate-600 font-medium">
                            {new Date(item.eventDate).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                            item.status === "published"
                              ? "bg-green-100 text-green-700"
                              : item.status === "cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>

                      {/* Image Preview */}
                      {item.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-40 object-cover rounded-lg shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex gap-2">
                          {item.status === "draft" ? (
                            <button
                              onClick={() => handlePublishEvent(item._id)}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Publish"
                            >
                              <Eye className="h-4 w-4" />
                              Publish
                            </button>
                          ) : item.status === "published" ? (
                            <button
                              onClick={() => handleUnpublishEvent(item._id)}
                              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Unpublish"
                            >
                              <EyeOff className="h-4 w-4" />
                              Unpublish
                            </button>
                          ) : null}
                          {item.status !== "cancelled" && (
                            <button
                              onClick={() => handleCancelEvent(item._id)}
                              className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm"
                              title="Cancel Event"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditEvent(item)}
                            className="flex-1 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(item)}
                            className="flex-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "staff":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Staff Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage staff members displayed on the landing page
                </p>
              </div>
              <button
                onClick={handleCreateStaff}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Staff
              </button>
            </div>
            {staffLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {staffData?.data?.map((item: any, index: number) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        {/* Order indicator on the left */}
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-slate-100 text-slate-600 rounded-full text-sm font-bold">
                            #{index + 1}
                          </span>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900">
                              {item.name}
                            </h4>
                            <p className="text-sm text-slate-600 font-medium">
                              {item.position}
                            </p>
                          </div>
                        </div>

                        {/* Reorder buttons and status on the right */}
                        <div className="flex flex-col items-end gap-2">
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                              item.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.status}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleMoveStaffUp(item)}
                              disabled={index === 0}
                              className={`p-1 rounded ${
                                index === 0
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-blue-600 hover:bg-blue-50"
                              }`}
                              title="Move up"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleMoveStaffDown(item)}
                              disabled={
                                index === (staffData?.data?.length || 0) - 1
                              }
                              className={`p-1 rounded ${
                                index === (staffData?.data?.length || 0) - 1
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-blue-600 hover:bg-blue-50"
                              }`}
                              title="Move down"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Image Preview */}
                      {item.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="w-full h-48 object-cover rounded-lg shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex gap-2">
                          {item.status === "inactive" ? (
                            <button
                              onClick={() => handleActivateStaff(item._id)}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Activate"
                            >
                              <Eye className="h-4 w-4" />
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeactivateStaff(item._id)}
                              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Deactivate"
                            >
                              <EyeOff className="h-4 w-4" />
                              Deactivate
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditStaff(item)}
                            className="flex-1 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteStaff(item)}
                            className="flex-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "courses":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Courses Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage courses displayed on the landing page
                </p>
              </div>
              <button
                onClick={handleCreateCourse}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Course
              </button>
            </div>
            {coursesLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {coursesData?.data?.map((item: any, index: number) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative"
                  >
                    {/* Order indicator */}
                    <div className="absolute top-4 left-4 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                      #{index + 1}
                    </div>

                    {/* Reorder buttons */}
                    <div className="absolute top-4 right-4 flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveCourseUp(item, index)}
                        disabled={index === 0}
                        className={`p-0.5 rounded-lg shadow-md transition-all ${
                          index === 0
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white text-blue-600 hover:bg-blue-50 hover:scale-110"
                        }`}
                        title="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMoveCourseDown(item, index)}
                        disabled={
                          index === (coursesData?.data?.length || 0) - 1
                        }
                        className={`p-1.5 rounded-lg shadow-md transition-all ${
                          index === (coursesData?.data?.length || 0) - 1
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white text-blue-600 hover:bg-blue-50 hover:scale-110"
                        }`}
                        title="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="p-6 pt-12">
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-lg font-bold text-slate-900 line-clamp-2 flex-1">
                            {item.title}
                          </h4>
                        </div>

                        {/* Image Preview */}
                        {item.imageUrl && (
                          <div className="mb-3">
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className="w-full h-40 object-cover rounded-lg shadow-md"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-slate-600 font-medium">
                            <span>{item.category}</span>
                            <span
                              className={`font-bold ${
                                item.pricing?.isFree
                                  ? "text-green-600"
                                  : "text-slate-900"
                              }`}
                            >
                              {item.pricing?.isFree
                                ? "Free"
                                : `${
                                    item.pricing?.currency
                                  } ${item.pricing?.amount?.toLocaleString()}`}
                            </span>
                          </div>
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                              item.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          {item.status === "inactive" ? (
                            <button
                              onClick={() => handleActivateCourse(item._id)}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Activate"
                            >
                              <Eye className="h-4 w-4" />
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeactivateCourse(item._id)}
                              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Deactivate"
                            >
                              <EyeOff className="h-4 w-4" />
                              Deactivate
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditCourse(item)}
                            className="flex-1 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCourse(item)}
                            className="flex-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "popups":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Popup Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage popups displayed on the landing page
                </p>
              </div>
              <button
                onClick={handleCreatePopup}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Popup
              </button>
            </div>
            {popupsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {popupsData?.data?.map((item: any) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                            {item.title}
                          </h4>
                          <div className="flex items-center gap-3 text-sm text-slate-600 font-medium">
                            <span
                              className={`px-2 py-0.5 rounded ${
                                item.contentType === "image"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {item.contentType}
                            </span>
                            <span>Priority: {item.priority}</span>
                          </div>
                        </div>
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                            item.status === "published"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>

                      {/* Image Preview */}
                      {item.image?.url && (
                        <div className="mb-4">
                          <img
                            src={item.image.url}
                            alt={item.image.alt || item.title}
                            className="w-full h-40 object-cover rounded-lg shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="mb-4 space-y-2 text-sm text-slate-600">
                        <div className="flex justify-between">
                          <span className="font-medium">Views:</span>
                          <span className="font-bold">
                            {item.statistics?.views || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Clicks:</span>
                          <span className="font-bold">
                            {item.statistics?.clicks || 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Dismissals:</span>
                          <span className="font-bold">
                            {item.statistics?.dismissals || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.status === "draft" ? (
                          <button
                            onClick={() => handlePublishPopup(item._id)}
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                            title="Publish"
                          >
                            <Eye className="h-4 w-4" />
                            Publish
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnpublishPopup(item._id)}
                            className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                            title="Unpublish"
                          >
                            <EyeOff className="h-4 w-4" />
                            Unpublish
                          </button>
                        )}
                        <button
                          onClick={() => handleEditPopup(item)}
                          className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePopup(item)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "partners":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Partners & Accreditation Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage partners and accreditations displayed on the landing
                  page
                </p>
              </div>
              <button
                onClick={handleCreatePartner}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Partner
              </button>
            </div>
            {partnersLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {partnersData?.data?.map((item: any, index: number) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative"
                  >
                    {/* Order indicator */}
                    <div className="absolute top-4 left-4 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                      #{index + 1}
                    </div>

                    {/* Reorder buttons */}
                    <div className="absolute top-4 right-4 flex flex-col gap-1">
                      <button
                        onClick={() => handleMovePartnerUp(item, index)}
                        disabled={index === 0}
                        className={`p-1.5 rounded-lg shadow-md transition-all ${
                          index === 0
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white text-blue-600 hover:bg-blue-50 hover:scale-110"
                        }`}
                        title="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMovePartnerDown(item, index)}
                        disabled={
                          index === (partnersData?.data?.length || 0) - 1
                        }
                        className={`p-1.5 rounded-lg shadow-md transition-all ${
                          index === (partnersData?.data?.length || 0) - 1
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white text-blue-600 hover:bg-blue-50 hover:scale-110"
                        }`}
                        title="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="p-6 pt-12">
                      {/* Partner Logo */}
                      {item.logo?.url && (
                        <div className="mb-4 flex justify-center">
                          <img
                            src={item.logo.url}
                            alt={item.logo.alt || item.name}
                            className="h-20 w-auto object-contain"
                          />
                        </div>
                      )}

                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-lg font-bold text-slate-900 line-clamp-2 flex-1">
                            {item.name}
                          </h4>
                        </div>
                        <div className="flex items-center justify-between">
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                              item.type === "accreditation"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {item.type}
                          </span>
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                              item.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex gap-2">
                          {item.status === "inactive" ? (
                            <button
                              onClick={() => handleActivatePartner(item._id)}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Activate"
                            >
                              <Eye className="h-4 w-4" />
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeactivatePartner(item._id)}
                              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Deactivate"
                            >
                              <EyeOff className="h-4 w-4" />
                              Deactivate
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditPartner(item)}
                            className="flex-1 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeletePartner(item)}
                            className="flex-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "badges":
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Floating Badges Management
                </h3>
                <p className="text-slate-600 font-medium mt-1">
                  Manage the floating activity badges (e.g. anniversary promo)
                  shown on the landing page
                </p>
              </div>
              <button
                onClick={handleCreateBadge}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add Floating Badge
              </button>
            </div>
            {badgesLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {badgesData?.data?.map((item: any, index: number) => (
                  <div
                    key={item._id}
                    className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative"
                  >
                    {/* Order indicator */}
                    <div className="absolute top-4 left-4 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                      #{index + 1}
                    </div>

                    {/* Reorder buttons */}
                    <div className="absolute top-4 right-4 flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveBadgeUp(item, index)}
                        disabled={index === 0}
                        className={`p-1.5 rounded-lg shadow-md transition-all ${
                          index === 0
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white text-blue-600 hover:bg-blue-50 hover:scale-110"
                        }`}
                        title="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMoveBadgeDown(item, index)}
                        disabled={
                          index === (badgesData?.data?.length || 0) - 1
                        }
                        className={`p-1.5 rounded-lg shadow-md transition-all ${
                          index === (badgesData?.data?.length || 0) - 1
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white text-blue-600 hover:bg-blue-50 hover:scale-110"
                        }`}
                        title="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="p-6 pt-12">
                      {/* Badge Image */}
                      {item.image?.url && (
                        <div className="mb-4 flex justify-center">
                          <img
                            src={item.image.url}
                            alt={item.image.alt || item.title}
                            className="h-20 w-20 object-cover rounded-full border border-gray-300"
                          />
                        </div>
                      )}

                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-lg font-bold text-slate-900 line-clamp-2 flex-1 text-center">
                            {item.title}
                          </h4>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-700">
                            {item.actionType}
                          </span>
                          <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-700">
                            {item.position}
                          </span>
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                              item.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.status}
                          </span>
                        </div>
                        {(item.startDate || item.endDate) && (
                          <p className="text-xs text-slate-500 text-center mt-2">
                            {item.startDate &&
                              `From ${new Date(
                                item.startDate
                              ).toLocaleDateString()}`}
                            {item.startDate && item.endDate && " "}
                            {item.endDate &&
                              `Until ${new Date(
                                item.endDate
                              ).toLocaleDateString()}`}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="flex gap-2">
                          {item.status === "inactive" ? (
                            <button
                              onClick={() => handleActivateBadge(item._id)}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Activate"
                            >
                              <Eye className="h-4 w-4" />
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeactivateBadge(item._id)}
                              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-3 py-2 rounded-lg font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 text-sm"
                              title="Deactivate"
                            >
                              <EyeOff className="h-4 w-4" />
                              Deactivate
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditBadge(item)}
                            className="flex-1 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteBadge(item)}
                            className="flex-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Landing Page Management
                </h1>
                <p className="text-slate-600 font-semibold mt-1">
                  Manage content displayed on the public landing page
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="h-5 w-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl mb-8 overflow-hidden">
          <nav className="flex flex-col sm:flex-row gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 font-bold text-sm transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                    : "text-slate-600 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
                {/* <span
                  className={`py-0.5 px-2 rounded-full text-xs font-bold ${
                    activeTab === tab.id
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {tab.count}
                </span> */}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">{renderContent()}</div>
      </div>

      {/* News Form Modal */}
      <NewsFormModal
        isOpen={showNewsModal}
        onClose={handleNewsModalClose}
        onSuccess={handleNewsSuccess}
        news={selectedNews}
      />

      {/* Event Form Modal */}
      <EventFormModal
        isOpen={showEventModal}
        onClose={handleEventModalClose}
        onSuccess={handleEventSuccess}
        event={selectedEvent}
      />

      {/* Staff Form Modal */}
      <StaffFormModal
        isOpen={showStaffModal}
        onClose={handleStaffModalClose}
        onSuccess={handleStaffSuccess}
        staff={selectedStaff}
      />

      {/* Course Form Modal */}
      <CourseFormModal
        isOpen={showCourseModal}
        onClose={handleCourseModalClose}
        onSuccess={handleCourseSuccess}
        course={selectedCourse}
      />

      {/* Popup Form Modal */}
      <PopupFormModal
        isOpen={showPopupModal}
        onClose={handlePopupModalClose}
        onSubmit={handlePopupSubmit}
        popup={selectedPopup}
        mode={selectedPopup ? "edit" : "create"}
      />

      {/* Partner Form Modal */}
      <PartnerFormModal
        isOpen={showPartnerModal}
        onClose={handlePartnerModalClose}
        onSubmit={handlePartnerSubmit}
        partner={selectedPartner}
        mode={selectedPartner ? "edit" : "create"}
      />

      {/* Floating Badge Form Modal */}
      <FloatingBadgeFormModal
        isOpen={showBadgeModal}
        onClose={handleBadgeModalClose}
        onSubmit={handleBadgeSubmit}
        badge={selectedBadge}
        mode={selectedBadge ? "edit" : "create"}
      />
    </div>
  );
};

export default LandingPageManagement;
